import { cn } from "@/lib/utils";
import { Editor } from "@tiptap/react";
import {
    AlignCenter,
    AlignLeft,
    AlignRight,
    Bold,
    Italic,
    Link as LinkIcon,
    List,
    ListOrdered,
    Strikethrough,
    Underline,
} from "lucide-react";
import { useState } from "react";
import { Fragment } from "react/jsx-runtime";
import EmojiPicker from "../EmojiPicker";
import { Button } from "../ui/button";
import LinkInputDialog from "../ui/link-input-dialog";
import {
    eventFormTone,
    type EventFormTone,
} from "../widgets/dashboard/calendar/eventModal/form/eventFormTone";

interface RichTextToolbarProps {
    editor: Editor;
    className?: string;
    /** The surface the toolbar sits on: the calendar's purple modal (default), or a white card. */
    tone?: EventFormTone;
}

const RichTextToolbar = ({
    editor,
    className,
    tone = "dark",
}: RichTextToolbarProps) => {
    const { toolHover, toolActive } = eventFormTone[tone];
    const [isLinkDialogOpen, setIsLinkDialogOpen] = useState(false);

    const handleLinkAdd = (url: string) => {
        const { from, to } = editor.state.selection;
        const chain = editor.chain().focus().setLink({ href: url });
        // When a range was linked, drop the cursor past the link and clear the
        // stored mark so text typed afterwards is not swallowed into the <a>.
        if (from !== to) {
            chain.setTextSelection(to).unsetMark("link");
        }
        chain.run();
    };

    const toolbarItems = [
        {
            id: 1,
            icon: Bold,
            isActive: () => editor.isActive("bold"),
            onClick: () => editor.chain().focus().toggleBold().run(),
        },
        {
            id: 2,
            icon: Italic,
            isActive: () => editor.isActive("italic"),
            onClick: () => editor.chain().focus().toggleItalic().run(),
        },
        {
            id: 3,
            icon: Underline,
            isActive: () => editor.isActive("underline"),
            onClick: () => editor.chain().focus().toggleUnderline().run(),
        },
        {
            id: 4,
            icon: Strikethrough,
            isActive: () => editor.isActive("strike"),
            onClick: () => editor.chain().focus().toggleStrike().run(),
        },
        {
            id: 5,
            icon: List,
            isActive: () => editor.isActive("bulletList"),
            onClick: () => editor.chain().focus().toggleBulletList().run(),
        },
        {
            id: 6,
            icon: ListOrdered,
            isActive: () => editor.isActive("orderedList"),
            onClick: () => editor.chain().focus().toggleOrderedList().run(),
        },
        {
            id: 7,
            icon: LinkIcon,
            isActive: () => editor.isActive("link"),
            onClick: () => {
                // Check if there's a link at the current selection
                if (editor.isActive("link")) {
                    // Remove the link from the selected text
                    editor.chain().focus().unsetLink().run();
                } else {
                    // Check if there's selected text
                    const { from, to } = editor.state.selection;
                    if (from === to) {
                        // No text selected, show dialog to enter both text and URL
                        setIsLinkDialogOpen(true);
                    } else {
                        // Text is selected, show dialog to enter URL
                        setIsLinkDialogOpen(true);
                    }
                }
            },
        },
        {
            id: 8,
            icon: AlignLeft,
            isActive: () => editor.isActive({ textAlign: "left" }),
            onClick: () => editor.chain().focus().setTextAlign("left").run(),
        },
        {
            id: 9,
            icon: AlignCenter,
            isActive: () => editor.isActive({ textAlign: "center" }),
            onClick: () => editor.chain().focus().setTextAlign("center").run(),
        },
        {
            id: 10,
            icon: AlignRight,
            isActive: () => editor.isActive({ textAlign: "right" }),
            onClick: () => editor.chain().focus().setTextAlign("right").run(),
        },
        {
            id: 11,
            icon: null,
            isActive: () => false,
            onClick: () => {},
            customComponent: (
                <EmojiPicker
                    className={cn("h-6 w-6 rounded-xl", toolHover)}
                    editor={editor}
                />
            ),
        },
    ];

    return (
        <>
            <div
                className={cn(
                    "flex w-full items-center justify-center gap-1 overflow-hidden rounded-tl-md rounded-tr-md border p-1",
                    tone === "dark"
                        ? "border-white/50"
                        : eventFormTone.light.border,
                    className,
                )}
            >
                {toolbarItems.map((item) => (
                    <Fragment key={item.id}>
                        {item.customComponent || (
                            <Button
                                className={cn(
                                    item.isActive() && toolActive,
                                    toolHover,
                                )}
                                onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    item.onClick();
                                }}
                                size="xs"
                                variant="ghost"
                            >
                                {item.icon && <item.icon className="h-4 w-4" />}
                            </Button>
                        )}
                    </Fragment>
                ))}
            </div>

            <LinkInputDialog
                isOpen={isLinkDialogOpen}
                onClose={() => setIsLinkDialogOpen(false)}
                onConfirm={handleLinkAdd}
            />
        </>
    );
};

export default RichTextToolbar;
